import React, { useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Slide,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TransitionProps } from "@mui/material/transitions";

import DbUpload from "./FileUpload";
import apiCall from "../utils/apiCall";
import getBase64 from "../utils/getBase64";
import validateFileName from "../utils/validateFileName";
import validateExcelFile from "../utils/validateExcelFile";

import { DATABASE, EXCEL, IMAGE, NEO4J, PDF, YOUTUBE } from "../data/constants";

import { useAppDispatch, useAppSelector } from "../hooks/reduxhook";

import { addFiles, getFiles, getType } from "../redux/slices/typeSlice";

import { loadingforindexes } from "../redux/slices/loadingSlice";
import { addError, addSuccessMessage } from "../redux/slices/errorSlice";

import ConfirmModal from "./ConfirmModal";
import Neo4jConnectionAdd from "./Neo4jConnectionAdd";

import { getModelDetails } from "../redux/slices/modelSlice";
import hyperscaler_mapping from "../data/modelMapping";
import { getMetadataFlag } from "../redux/slices/userSlice";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface IValue {
  base64: string;
  name: string;
  filetype: string;
  size?: number;
}

interface IBulkPdfFile extends IValue {
  id: string;
  originalName: string;
  indexName: string;
  machineReadable: boolean;
  model: string;
}

interface IChatDbUpload {
  open: boolean;
  setOpen: (open: boolean) => void;
  refreshFiles: () => void;
}

interface IBulkPdfPayloadFile {
  chunk_name: string;
  pdf: string;
  file_type: string;
  model: string;
  metadata_flag: boolean;
}

const UploadFile = ({ open, setOpen, refreshFiles }: IChatDbUpload) => {
  const { accounts } = useMsal();
  const accountId = accounts[0]?.username;
  const [isUploading, setIsUploading] = useState(false);
  const models = useAppSelector(getModelDetails);
  const chatType = useAppSelector(getType);
  const files = useAppSelector(getFiles);
  const metadataFlag = useAppSelector(getMetadataFlag);
  const dispatch = useAppDispatch();

  const defaultImageModel = models?.models?.multimodal?.at(0)?.model_name || "";

  const [value, setValue] = useState<IValue | null>(null);

  const [bulkPdfFiles, setBulkPdfFiles] = useState<IBulkPdfFile[]>([]);

  const [name, setName] = useState("");
  const [sqlconfig, setSqlconfig] = useState<any>();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [confirmReplace, setConfirmReplace] = useState("");

  const [connectionString, setConnectionString] = useState("");

  const [unprocessable, setUnprocessable] = useState(false);

  const [imageModel, setImageModel] = useState(defaultImageModel);

  useEffect(() => {
    if (!imageModel && defaultImageModel) {
      setImageModel(defaultImageModel);
    }
  }, [defaultImageModel, imageModel]);

  const selectImageModel = (
    event: React.ChangeEvent<{ value: unknown }> | any,
  ) => {
    setImageModel(event.target.value);
  };

  const createTemporaryId = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  };

  const removeFileExtension = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf(".");

    if (lastDotIndex <= 0) {
      return fileName;
    }

    return fileName.substring(0, lastDotIndex);
  };

  const normalizeIndexName = (indexName: string) => {
    return indexName.trim().replace(/\s+/g, "_");
  };

  const resetForm = () => {
    setValue(null);
    setBulkPdfFiles([]);
    setName("");
    setSqlconfig(undefined);
    setConnectionString("");
    setUnprocessable(false);
    setImageModel(defaultImageModel);
    setOpenConfirmModal(false);
    setConfirmReplace("");
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
  };

  const updateBulkPdfFile = (
    id: string,
    field: keyof IBulkPdfFile,
    newValue: string | boolean,
  ) => {
    setBulkPdfFiles((previousFiles) =>
      previousFiles.map((pdfFile) =>
        pdfFile.id === id
          ? {
              ...pdfFile,
              [field]: newValue,
            }
          : pdfFile,
      ),
    );
  };

  const removeBulkPdfFile = (id: string) => {
    setBulkPdfFiles((previousFiles) =>
      previousFiles.filter((pdfFile) => pdfFile.id !== id),
    );
  };

  const processPdfFiles = async (selectedFiles: FileList) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      dispatch(addError("Please select at least one PDF file"));
      return;
    }

    const processedFiles: IBulkPdfFile[] = [];
    const existingSelectedNames = new Set(
      bulkPdfFiles.map((pdfFile) => pdfFile.originalName.toLowerCase()),
    );

    for (let index = 0; index < selectedFiles.length; index += 1) {
      const selectedFile = selectedFiles[index];

      const validation = validateFileName(
        selectedFile,
        chatType?.uploadtypes || [],
      );

      if (!validation.isValid) {
        dispatch(addError(`${selectedFile.name}: ${validation.message}`));
        continue;
      }

      if (selectedFile.size >= 4 * 1024 * 1024) {
        dispatch(
          addError(`${selectedFile.name}: File size exceeds the 4MB limit`),
        );
        continue;
      }

      if (existingSelectedNames.has(selectedFile.name.toLowerCase())) {
        dispatch(addError(`${selectedFile.name} is already selected`));
        continue;
      }

      try {
        const base64Result = await getBase64(selectedFile);

        const safeNameWithoutExtension = removeFileExtension(
          validation.safeFileName || selectedFile.name,
        );

        processedFiles.push({
          id: createTemporaryId(),
          originalName: selectedFile.name,
          name: safeNameWithoutExtension,
          indexName: safeNameWithoutExtension,
          filetype: validation.extension,
          base64: base64Result.split(",")[1].toString(),
          size: selectedFile.size,
          machineReadable: false,
          model: defaultImageModel,
        });

        existingSelectedNames.add(selectedFile.name.toLowerCase());
      } catch (error) {
        console.error(`Failed to process ${selectedFile.name}`, error);

        dispatch(addError(`Failed to process ${selectedFile.name}`));
      }
    }

    if (processedFiles.length > 0) {
      setBulkPdfFiles((previousFiles) => [...previousFiles, ...processedFiles]);
    }
  };

  const processSingleFile = async (selectedFiles: FileList) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      dispatch(addError("Please select a file"));
      return;
    }

    const selectedFile = selectedFiles[0];

    if (chatType.type === DATABASE) {
      setSqlconfig(selectedFiles);
    }

    const validation = validateFileName(
      selectedFile,
      chatType?.uploadtypes || [],
    );

    if (!validation.isValid) {
      dispatch(addError(validation.message));
      return;
    }

    if (chatType.type === EXCEL) {
      try {
        const excelValidation = await validateExcelFile(selectedFile);

        if (!excelValidation.isValid) {
          dispatch(addError(excelValidation.message));
          return;
        }
      } catch (error) {
        dispatch(addError("Failed to validate Excel file. Please try again."));
        return;
      }
    }

    try {
      const base64Result = await getBase64(selectedFile);
      const fileSize = selectedFile.size;
      const fileSizeInKB = fileSize / 1024;
      const fileSizeInMB = fileSize / (1024 * 1024);

      if (chatType.type === EXCEL) {
        const maximumSizeInKB = 1024;
        const preferredSizeInKB = 500;

        if (fileSizeInKB > maximumSizeInKB) {
          dispatch(
            addError(
              `File size exceeds the maximum limit of 1MB. Current size: ${Math.round(
                fileSizeInKB,
              )}KB. Please reduce file size and try again.`,
            ),
          );
          return;
        }

        if (fileSizeInKB > preferredSizeInKB) {
          console.warn(
            `File size (${Math.round(
              fileSizeInKB,
            )}KB) exceeds the recommended limit of 500KB.`,
          );
        }
      } else {
        const sizeLimit = 4;

        if (fileSizeInMB >= sizeLimit) {
          dispatch(addError(`File size exceeds the ${sizeLimit}MB limit`));
          return;
        }
      }

      const safeNameWithoutExtension = removeFileExtension(
        validation.safeFileName || selectedFile.name,
      );

      setValue({
        base64: base64Result.split(",")[1].toString(),
        filetype: validation.extension,
        name: safeNameWithoutExtension,
        size: selectedFile.size,
      });

      setName(safeNameWithoutExtension);
    } catch (error) {
      console.error("File processing error:", error);

      dispatch(addError("Failed to process the file. Please try again."));
    }
  };

  const handleSelectedFiles = async (selectedFiles: FileList) => {
    if (chatType.type === PDF) {
      await processPdfFiles(selectedFiles);
      return;
    }

    await processSingleFile(selectedFiles);
  };

  const validateBulkPdfFiles = () => {
    if (bulkPdfFiles.length === 0) {
      dispatch(addError("Please select at least one PDF file"));
      return false;
    }

    if (!accountId) {
      dispatch(addError("User email not found"));
      return false;
    }

    const indexNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_ ]*$/;

    for (const pdfFile of bulkPdfFiles) {
      if (!pdfFile.base64?.trim()) {
        dispatch(addError(`${pdfFile.originalName}: File content is empty`));
        return false;
      }

      if (!pdfFile.indexName.trim()) {
        dispatch(
          addError(`${pdfFile.originalName}: Please enter an index name`),
        );
        return false;
      }

      if (!indexNameRegex.test(pdfFile.indexName.trim())) {
        dispatch(
          addError(
            `${pdfFile.originalName}: Index name must use only letters, numbers, spaces or underscores and must start with a letter or number`,
          ),
        );
        return false;
      }

      if (pdfFile.machineReadable && !pdfFile.model) {
        dispatch(
          addError(`${pdfFile.originalName}: Please select an image model`),
        );
        return false;
      }
    }

    const normalizedNames = bulkPdfFiles.map((pdfFile) =>
      normalizeIndexName(pdfFile.indexName).toLowerCase(),
    );

    const uniqueNames = new Set(normalizedNames);

    if (uniqueNames.size !== normalizedNames.length) {
      dispatch(addError("Each uploaded file must have a unique index name"));
      return false;
    }

    return true;
  };

  const uploadBulkPdfFiles = async () => {
    if (!validateBulkPdfFiles()) {
      return;
    }

    setIsUploading(true);
    dispatch(loadingforindexes(true));
    /*
     * The payload below follows the exact backend contract:
     *
     * {
     *   files: [
     *     {
     *       chunk_name: string,
     *       pdf: string,
     *       file_type: string,
     *       model: string
     *     }
     *   ]
     * }
     *
     * The Machine Readable switch controls whether the selected
     * image model is sent. When it is off, model is sent as an
     * empty string.
     */

    const payload: {
      files: IBulkPdfPayloadFile[];
    } = {
      files: bulkPdfFiles.map((pdfFile) => ({
        chunk_name:
          normalizeIndexName(pdfFile.indexName) +
          "---" +
          accountId +
          "---" +
          pdfFile.filetype,

        pdf: pdfFile.base64,

        file_type: pdfFile.filetype,

        model: pdfFile.machineReadable ? pdfFile.model : "",
        metadata_flag: Boolean(metadataFlag),
      })),
    };

    // console.log("Bulk PDF upload payload:", {
    //   files: payload.files.map((file) => ({
    //     ...file,
    //     pdf: `${file.pdf.substring(0, 50)}...`,
    //   })),
    // });

    dispatch(loadingforindexes(true));

    try {
      const response = await apiCall("POST", chatType.loadurl, payload);

      if (Array.isArray(response?.result)) {
        dispatch(addFiles(response.result));
      } else if (response?.result) {
        dispatch(addFiles(response.result));
      }

      const successCount = response?.success_count || 0;
      const failedCount = response?.failed_count || 0;
      const totalFiles = response?.total_files || bulkPdfFiles.length;

      let message = `${successCount}/${totalFiles} files uploaded successfully`;

      if (failedCount > 0) {
        const failedFileNames =
          response?.failed_files?.join(", ") || "Some files";

        message += `\n\nFailed Files:\n${failedFileNames}`;
      }
      refreshFiles();

      dispatch(addSuccessMessage(message));

      handleClose();
    } catch (error: any) {
      console.error("Bulk PDF upload error:", error);

      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while uploading PDF files";

      dispatch(addError(errorMessage));
    } finally {
      setIsUploading(false);
      dispatch(loadingforindexes(false));
    }
  };

  const uploadSingleFile = (confirm = false) => {
    if (chatType.type === IMAGE) {
      if (!value?.base64) {
        dispatch(addError("Please select an image"));
        return;
      }

      const imageFileName = name.trim() + "---" + value.filetype;

      if (files?.includes(imageFileName) && confirm !== true) {
        setOpenConfirmModal(true);
        setConfirmReplace(imageFileName);
        return;
      }

      dispatch(loadingforindexes(true));

      apiCall("POST", chatType.loadurl, {
        image: {
          base64_string: value.base64,
          filename: imageFileName,
        },
        user_email: accountId,
      })
        .then((response) => {
          dispatch(addFiles(response.result));
          handleClose();
        })
        .catch((error) => {
          console.error("Image upload error:", error);

          dispatch(
            addError(
              error?.response?.data?.detail ||
                error?.response?.data?.message ||
                "Something went wrong while uploading image",
            ),
          );
        })
        .finally(() => {
          dispatch(loadingforindexes(false));
        });

      return;
    }

    if (chatType.type === EXCEL) {
      if (!name.trim()) {
        dispatch(addError("Please enter a file name"));
        return;
      }

      if (!accountId) {
        dispatch(addError("User email not found"));
        return;
      }

      if (!value?.base64?.trim()) {
        dispatch(addError("No file selected or file content is empty"));
        return;
      }

      const indexNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_ ]*$/;

      if (!indexNameRegex.test(name.trim())) {
        dispatch(
          addError(
            "File name must use only letters, numbers, spaces or underscores and must start with a letter or number",
          ),
        );
        return;
      }

      const excelFileName = name.trim() + "." + value.filetype;

      if (files?.includes(excelFileName) && confirm !== true) {
        setOpenConfirmModal(true);
        setConfirmReplace(excelFileName);
        return;
      }

      dispatch(loadingforindexes(true));

      apiCall("POST", chatType.loadurl, {
        file_name: excelFileName,
        excel: value.base64,
      })
        .then((response) => {
          dispatch(addFiles(response.result));
          handleClose();
        })
        .catch((error) => {
          console.error("Excel upload error:", error);

          dispatch(
            addError(
              error?.response?.data?.detail ||
                error?.response?.data?.message ||
                "Something went wrong while uploading Excel file",
            ),
          );
        })
        .finally(() => {
          dispatch(loadingforindexes(false));
        });

      return;
    }

    if (chatType.type === DATABASE) {
      if (!name.trim() || !connectionString.trim()) {
        dispatch(
          addError("Please enter the database name and connection string"),
        );
        return;
      }

      dispatch(loadingforindexes(true));

      apiCall("POST", chatType.loadurl, {
        connection_string: connectionString.trim(),
        name: name.trim(),
        user_email: accountId,
      })
        .then((response) => {
          dispatch(addFiles(response.result));
          handleClose();
        })
        .catch((error) => {
          console.error("Database error:", error);

          dispatch(
            addError(
              error?.response?.data?.detail ||
                error?.response?.data?.message ||
                "Something went wrong while adding database",
            ),
          );
        })
        .finally(() => {
          dispatch(loadingforindexes(false));
        });

      return;
    }

    if (chatType.type === YOUTUBE) {
      if (!name.trim() || !connectionString.trim()) {
        dispatch(addError("Please enter the video name and URL"));
        return;
      }

      dispatch(loadingforindexes(true));

      apiCall("POST", chatType.loadurl, {
        url: connectionString.trim(),
        video_name: name.trim() + "---" + accountId + "---yt",
        user_email: accountId,
      })
        .then((response) => {
          dispatch(addFiles(response.result));
          handleClose();
        })
        .catch((error) => {
          console.error("YouTube upload error:", error);

          dispatch(
            addError(
              error?.response?.data?.detail ||
                error?.response?.data?.message ||
                "Something went wrong while adding video",
            ),
          );
        })
        .finally(() => {
          dispatch(loadingforindexes(false));
        });
    }
  };

  const uploadDb = (confirm = false) => {
    if (chatType.type === PDF) {
      uploadBulkPdfFiles();
      return;
    }

    uploadSingleFile(confirm);
  };

  const isUploadDisabled = () => {
    if (chatType.type === PDF) {
      if (bulkPdfFiles.length === 0) {
        return true;
      }

      return bulkPdfFiles.some(
        (pdfFile) =>
          !pdfFile.base64 ||
          !pdfFile.indexName.trim() ||
          (pdfFile.machineReadable && !pdfFile.model),
      );
    }

    if (chatType.type === DATABASE || chatType.type === YOUTUBE) {
      return !name.trim() || !connectionString.trim();
    }

    return !value?.base64 || !name.trim();
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="upload-file-dialog"
      fullWidth
      maxWidth={chatType.type === PDF ? "lg" : "sm"}
    >
      <DialogContent sx={{ p: 4 }}>
        {chatType.type === DATABASE || chatType.type === YOUTUBE ? (
          <>
            <TextField
              value={connectionString}
              onChange={(event) => setConnectionString(event.target.value)}
              label={
                chatType.type === DATABASE
                  ? "Enter Connection String"
                  : "Enter URL"
              }
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
            />

            <TextField
              value={name}
              onChange={(event) => setName(event.target.value)}
              label={chatType.type === DATABASE ? "DB Name" : "Name"}
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
            />

            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 4 }}
              disabled={isUploadDisabled()}
              onClick={() => uploadDb(false)}
              color="secondary"
            >
              {chatType.type === DATABASE ? "Add Database" : "Add Video"}
            </Button>
          </>
        ) : chatType.type === NEO4J ? (
          <Neo4jConnectionAdd
            url={chatType?.loadurl}
            handleClose={handleClose}
          />
        ) : (
          <>
            <DbUpload
              content={
                chatType.type === PDF
                  ? "Drag and drop multiple files"
                  : "Drag and drop a file"
              }
              setFile={handleSelectedFiles}
              file={value}
              files={bulkPdfFiles}
              multiple={chatType.type === PDF}
            />

            {chatType.type === PDF ? (
              <>
                {bulkPdfFiles.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Selected files
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Edit the index name and enable Machine Readable when the
                      document contains scanned pages or images.
                    </Typography>

                    <TableContainer
                      sx={{
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 1,
                        maxHeight: 420,
                      }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                minWidth: 190,
                              }}
                            >
                              File Name
                            </TableCell>

                            <TableCell
                              sx={{
                                fontWeight: 600,
                                minWidth: 220,
                              }}
                            >
                              Index Name
                            </TableCell>

                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                minWidth: 150,
                              }}
                            >
                              Machine Readable
                            </TableCell>

                            <TableCell
                              sx={{
                                fontWeight: 600,
                                minWidth: 210,
                              }}
                            >
                              Image Model
                            </TableCell>

                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                width: 70,
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {bulkPdfFiles.map((pdfFile) => (
                            <TableRow key={pdfFile.id} hover>
                              <TableCell>
                                <Tooltip title={pdfFile.originalName}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: 190,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {pdfFile.originalName}
                                  </Typography>
                                </Tooltip>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {pdfFile.size
                                    ? `${(pdfFile.size / 1024).toFixed(1)} KB`
                                    : ""}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <TextField
                                  value={pdfFile.indexName}
                                  onChange={(event) =>
                                    updateBulkPdfFile(
                                      pdfFile.id,
                                      "indexName",
                                      event.target.value,
                                    )
                                  }
                                  size="small"
                                  fullWidth
                                  placeholder="Enter index name"
                                  error={!pdfFile.indexName.trim()}
                                  inputProps={{
                                    maxLength: 100,
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center">
                                <Switch
                                  color="secondary"
                                  checked={pdfFile.machineReadable}
                                  onChange={(event) =>
                                    updateBulkPdfFile(
                                      pdfFile.id,
                                      "machineReadable",
                                      event.target.checked,
                                    )
                                  }
                                />
                              </TableCell>

                              <TableCell>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  disabled={!pdfFile.machineReadable}
                                >
                                  <Select
                                    value={pdfFile.model}
                                    onChange={(event) =>
                                      updateBulkPdfFile(
                                        pdfFile.id,
                                        "model",
                                        event.target.value,
                                      )
                                    }
                                    displayEmpty
                                  >
                                    <MenuItem value="">Select model</MenuItem>

                                    {models?.models?.multimodal?.map(
                                      (model, modelIndex) => (
                                        <MenuItem
                                          key={`${model?.model_name}-${modelIndex}`}
                                          value={model?.model_name}
                                          sx={{
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          <Box
                                            component="span"
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <img
                                              src={
                                                hyperscaler_mapping[
                                                  `${
                                                    model?.model?.split(
                                                      "/",
                                                    )[0] ?? "psl"
                                                  }`
                                                ]
                                              }
                                              alt={model?.model_name}
                                              width="15"
                                              height="15"
                                            />

                                            {model?.model_name
                                              ?.replaceAll(
                                                process.env
                                                  ?.REACT_APP_DEFAULT_MODEL_REMOVE ??
                                                  "",
                                                "",
                                              )
                                              .replaceAll("-", " ")}
                                          </Box>
                                        </MenuItem>
                                      ),
                                    )}
                                  </Select>
                                </FormControl>
                              </TableCell>

                              <TableCell align="center">
                                <Tooltip title="Remove file">
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      removeBulkPdfFile(pdfFile.id)
                                    }
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Grid container justifyContent="center">
                  <TextField
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    label={
                      chatType.type === IMAGE
                        ? "Name"
                        : chatType.type === EXCEL
                          ? "Excel File Name"
                          : "File Name"
                    }
                    variant="outlined"
                    fullWidth
                    helperText={
                      chatType.type === EXCEL ? (
                        <div className="exceltext">
                          <span>
                            Only one Microsoft Excel/CSV file can be uploaded at
                            a time.
                          </span>
                          <br />

                          <span>
                            Supported formats: .xls, .xlsx, .csv. Maximum 1 MB,
                            500 KB recommended.
                          </span>
                          <br />

                          <span>File must contain only one worksheet.</span>
                          <br />

                          <span>Images in Excel files are not supported.</span>
                        </div>
                      ) : (
                        ""
                      )
                    }
                    sx={{
                      mt: 2,
                      ".MuiFormHelperText-root": {
                        color: "rgba(255,0,0,0.5)",
                        fontSize: "11px",
                      },
                    }}
                  />
                </Grid>

                {chatType.type === PDF && (
                  <Grid
                    container
                    direction="column"
                    sx={{
                      borderTop: "1px solid rgba(0,0,0,0.1)",
                      mt: 2,
                    }}
                  >
                    <Grid container alignItems="center">
                      <Switch
                        color="secondary"
                        onChange={(event) =>
                          setUnprocessable(event.target.checked)
                        }
                        checked={unprocessable}
                      />

                      <Typography
                        fontSize="small"
                        color="secondary"
                        sx={{ flex: 1 }}
                      >
                        Need support for image read.
                      </Typography>
                    </Grid>

                    {unprocessable && (
                      <Grid>
                        <Typography fontSize="small" sx={{ mt: 2 }}>
                          Select an image model to process
                        </Typography>

                        <Select
                          value={imageModel}
                          onChange={selectImageModel}
                          size="small"
                          sx={{
                            width: "100%",
                            fontSize: "11px",
                          }}
                        >
                          {models?.models?.multimodal?.map(
                            (model, modelIndex) => (
                              <MenuItem
                                key={modelIndex}
                                value={model?.model_name}
                              >
                                {model?.model_name}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </Grid>
                    )}
                  </Grid>
                )}
              </>
            )}

            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 4 }}
              disabled={isUploadDisabled()}
              onClick={() => uploadDb(false)}
              color="secondary"
            >
              {chatType.type === PDF
                ? `Upload ${bulkPdfFiles.length || ""} File${
                    bulkPdfFiles.length === 1 ? "" : "s"
                  }`
                : "Upload File"}
            </Button>
          </>
        )}
      </DialogContent>

      <ConfirmModal
        open={openConfirmModal}
        setOpen={() => {
          setOpenConfirmModal(false);
          setOpen(true);
        }}
        onclick={() => {
          setOpenConfirmModal(false);

          apiCall("POST", chatType.deleteurl, {
            chunk_name: confirmReplace,
            image_name: confirmReplace,
            file_name: confirmReplace,
            user_email: accountId,
          })
            .then(() => {
              uploadSingleFile(true);
            })
            .catch((error) => {
              console.error("Replace file error:", error);

              dispatch(
                addError(
                  error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Something went wrong while replacing the file",
                ),
              );
            });
        }}
      />
    </Dialog>
  );
};

export default UploadFile;
